import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Navbar from "../Navbar";
import { CTASection, Footer } from "../CTAFooter";
import { blogPosts, SITE } from "../../data/mock";

const BASE_URL = "https://abhinavjoshi.com";

// Renders one typed content block (see the shape documented above blogPosts in mock.js).
const Block = ({ block, index }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-12 mb-4 text-[24px] sm:text-[28px] font-semibold tracking-[-0.01em] text-neutral-950">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-8 mb-3 text-[19px] sm:text-[21px] font-semibold text-neutral-950">
          {block.text}
        </h3>
      );
    case "h4":
      return (
        <h4 className="mt-6 mb-2 text-[17px] font-semibold text-neutral-950">
          {block.text}
        </h4>
      );
    case "p":
      return <p className="text-neutral-700 leading-relaxed mb-4">{block.text}</p>;
    case "ul":
      return (
        <ul className="mb-5 space-y-2 list-disc pl-5">
          {block.items.map((item, i) => (
            <li key={i} className="text-neutral-700 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mb-5 space-y-2 list-decimal pl-5">
          {block.items.map((item, i) => (
            <li key={i} className="text-neutral-700 leading-relaxed">
              {item}
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-black/10">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-neutral-50 border-b border-black/10">
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left font-semibold text-neutral-950 px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-black/5 last:border-0">
                  {row.map((cell, ci) => (
                    <td key={ci} className="align-top text-neutral-700 px-4 py-3 leading-relaxed">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "image":
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt={block.alt}
            loading="lazy"
            className="w-full rounded-2xl border border-black/5"
          />
          {block.caption && (
            <figcaption className="mt-3 text-sm text-neutral-500 text-center">{block.caption}</figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  // Basic on-page SEO: unique <title>, meta description, and canonical URL per post.
  // Done via plain DOM updates so no extra dependency (e.g. react-helmet) is needed.
  useEffect(() => {
    if (!post) return;

    const prevTitle = document.title;
    document.title = `${post.title} | ${SITE.name}`;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute("content") : null;
    if (metaDesc) metaDesc.setAttribute("content", post.metaDescription || post.excerpt);

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevCanonicalHref = canonical.getAttribute("href");
    canonical.setAttribute("href", `${BASE_URL}/blog/${post.slug}`);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute("content", prevDesc);
      if (createdCanonical) {
        canonical.remove();
      } else if (prevCanonicalHref) {
        canonical.setAttribute("href", prevCanonicalHref);
      }
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white font-sans text-neutral-900">
        <Navbar />
        <div className="max-w-[700px] mx-auto px-6 py-24 text-center">
          <h1 className="text-[28px] font-semibold text-neutral-950">Post not found</h1>
          <p className="mt-4 text-neutral-600">This article doesn't exist or may have been moved.</p>
          <Link
            to="/category/blogs"
            className="mt-8 inline-flex items-center gap-1.5 bg-black text-white pl-5 pr-4 py-2.5 rounded-full text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all posts
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      <Navbar />

      <section className="pt-12 pb-16">
        <div className="max-w-[760px] mx-auto px-6">
          <Link
            to="/category/blogs"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-950 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Blogs
          </Link>

          <div className="mt-6 flex items-center gap-3 text-sm text-neutral-500">
            {post.date && <span>{post.date}</span>}
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 border border-black/10 rounded-full px-3 py-1">
              {post.category}
            </span>
          </div>

          {/* Single H1 per page: the post title */}
          <h1 className="mt-4 text-[30px] sm:text-[42px] leading-[1.1] tracking-[-0.02em] font-semibold text-neutral-950">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-neutral-600">{post.excerpt}</p>

          {post.image && (
            <img
              src={post.image.src}
              alt={post.image.alt}
              loading="eager"
              className="mt-8 w-full rounded-3xl border border-black/5"
            />
          )}

          <div className="mt-10">
            {post.body ? (
              <div className="max-w-none">
                {post.body.map((block, i) => (
                  <Block key={i} block={block} index={i} />
                ))}

                {post.ctaText && (
                  <div className="mt-12 rounded-3xl border border-black/10 bg-neutral-50 p-8 text-center">
                    <p className="text-neutral-700 leading-relaxed">{post.ctaText}</p>
                    <a
                      href={post.ctaHref}
                      className="mt-6 inline-flex items-center gap-1.5 bg-black text-white pl-5 pr-4 py-3 rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      {post.ctaLabel} <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-3xl border border-black/10 bg-neutral-50 p-10 text-center">
                <Clock className="w-6 h-6 text-neutral-400 mx-auto" />
                <p className="mt-4 text-neutral-600">
                  This article is being finalized. The full write-up is coming soon.
                </p>
                <a
                  href="/connect"
                  className="mt-6 inline-flex items-center gap-1.5 bg-black text-white pl-5 pr-4 py-2.5 rounded-full text-sm"
                >
                  Talk to me directly instead <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-neutral-50">
          <div className="max-w-[1280px] mx-auto px-6">
            <h2 className="text-lg font-semibold text-neutral-950 mb-6">Related posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <a
                  key={r.slug}
                  href={r.body ? `/blog/${r.slug}` : "/category/blogs"}
                  className="bg-white rounded-3xl p-6 border border-black/5 hover:shadow-lg transition-all"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                    {r.category}
                  </span>
                  <h3 className="mt-2 font-semibold text-neutral-950 leading-snug">{r.title}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
      <Footer />
    </div>
  );
};

export default BlogDetail;
